import { MusicRuntime } from "./runtime";
import type { ReactivityMode } from "./state";

type DebugToggleSurface = "desktop" | "mobile";

type DebugToggleOptions = {
  enabled: boolean;
  runtime: MusicRuntime;
  surface: DebugToggleSurface;
};

const STORAGE_KEY = "pfx_music_reactivity_mode_debug";
const MODES: ReactivityMode[] = ["legacy", "hybrid", "fluid"];

const isMode = (value: string | null): value is ReactivityMode => value === "legacy" || value === "hybrid" || value === "fluid";

export function readInitialDebugReactivityMode(params: URLSearchParams): ReactivityMode {
  const urlMode = params.get("reactivity") ?? params.get("music-mode");
  if (isMode(urlMode)) return urlMode;
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (isMode(stored)) return stored;
  } catch {
    /* ignore storage restrictions */
  }
  return "legacy";
}

export function initMusicDebugToggle(options: DebugToggleOptions): void {
  if (!options.enabled) return;
  if (document.getElementById("pfxMusicDebugToggle")) return;

  const host = findHost(options.surface);
  if (!host) return;

  const section = document.createElement("section");
  section.id = "pfxMusicDebugToggle";
  section.className = options.surface === "mobile" ? "pfxMusicDebug pfxMusicDebug--mobile" : "group pfxMusicDebug";

  const title = document.createElement("h2");
  title.textContent = "Reactivity Debug";

  const segmented = document.createElement("div");
  segmented.className = "pfxMusicDebug__seg";
  segmented.setAttribute("role", "radiogroup");
  segmented.setAttribute("aria-label", "Music reactivity mode");

  const status = document.createElement("p");
  status.className = "pfxMusicDebug__status";

  const setActive = (mode: ReactivityMode): void => {
    options.runtime.setMode(mode);
    try {
      localStorage.setItem(STORAGE_KEY, mode);
    } catch {
      /* ignore storage restrictions */
    }
    segmented.querySelectorAll<HTMLButtonElement>("button[data-reactivity-mode]").forEach((button) => {
      const active = button.dataset.reactivityMode === mode;
      button.classList.toggle("is-active", active);
      button.setAttribute("aria-checked", active ? "true" : "false");
    });
    updateStatus(status, options.runtime.read());
  };

  MODES.forEach((mode) => {
    const button = document.createElement("button");
    button.type = "button";
    button.className = "pfxMusicDebug__opt";
    button.dataset.reactivityMode = mode;
    button.setAttribute("role", "radio");
    button.textContent = mode[0].toUpperCase() + mode.slice(1);
    button.addEventListener("click", () => setActive(mode));
    segmented.appendChild(button);
  });

  section.append(title, segmented, status);
  host.appendChild(section);
  setActive(options.runtime.read().mode);
  window.setInterval(() => updateStatus(status, options.runtime.read()), 500);
}

function updateStatus(status: HTMLElement, state: ReturnType<MusicRuntime["read"]>): void {
  status.textContent = `${state.mode} | ${state.source.qualityMode} | ${state.source.kind}`;
}

function findHost(surface: DebugToggleSurface): HTMLElement | null {
  if (surface === "mobile") {
    return document.querySelector<HTMLElement>("#mbTab-settings") ?? document.querySelector<HTMLElement>("#mbTab-source");
  }
  return document.querySelector<HTMLElement>('.sidebarPanel[data-panel="source"]') ?? document.querySelector<HTMLElement>("#controls");
}
